<aside class="sidebar">
  <!-- Logo Section -->
  <div class="sidebar-header">
    <a routerLink="/dashboard" class="d-flex align-items-center gap-3" title="Go to Dashboard" (click)="goBackFromSubmenu()">
      <img src="assets/images/dark-logo-horizontal.svg" alt="American Drone Network" class="logo-icon" />
    </a>
    <!-- Close button for mobile -->
    <button class="sidebar-close d-lg-none" (click)="closeSidebar()" aria-label="Close menu">
      <i class="bi bi-x-lg"></i>
    </button>
  </div>

  <!-- Compact Onboarding Progress (unified, hidden in drill-down submenu) -->
  <div class="sidebar-onboarding mx-3 my-0 py-3 border-bottom"
       *ngIf="!activeSubmenu && showCompactOnboarding()">
    <div class="onboarding-compact">
      <div class="onboarding-compact-header d-flex justify-content-between align-items-center mb-2">
        <span class="onboarding-compact-title">Getting Started</span>
        <a class="onboarding-compact-link" (click)="restoreOnboarding()">View</a>
      </div>
      <div class="onboarding-compact-progress d-flex align-items-center gap-2">
        <div class="progress flex-grow-1 h-4px">
          <div class="progress-bar"
               [style.width.%]="combinedPercentage()">
          </div>
        </div>
        <span class="onboarding-compact-text">
          {{ combinedPercentage() }}% Complete
        </span>
      </div>
    </div>
  </div>

  <!-- Main Navigation (hidden when drill-down submenu is active) -->
  <nav class="sidebar-nav" *ngIf="!activeSubmenu">
    <div *ngFor="let section of navSections" class="nav-section">
      <ul class="nav-list">
        <li
          *ngFor="let item of section.items"
          class="nav-item-wrapper"
          [hidden]="!isVisible(item)"
        >
          <!-- Parent nav item -->
          <div
            class="nav-item"
            [class.active]="item.active && (!hasChildren(item) || item.drillDown)"
            [class.coming-soon]="item.comingSoon"
            [class.has-children]="hasChildren(item)"
            [class.expanded]="item.expanded"
            (click)="navigateTo(item, $event)"
          >
            <div class="nav-item-content">
              <div class="position-relative d-flex align-items-center justify-content-center">
                <span *ngIf="item.customIcon" class="nav-custom-icon" [style.--icon-url]="'url(' + item.customIcon + ')'" aria-hidden="true"></span>
                <i *ngIf="!item.customIcon" [class]="'bi ' + item.icon"></i>
                <span *ngIf="item.badge && (item.route !== '/activity' || hasUnseenActivity())" class="nav-badge"></span>
              </div>
              <span class="nav-label">{{ item.dynamicBilling ? billingLabel() : item.label }}</span>
              <i *ngIf="isLocked(item)" class="bi bi-lock-fill nav-lock-icon" role="img" aria-label="Pro feature"></i>
            </div>
            <!-- Chevron for expandable items (inline expand uses up/down, drill-down uses right) -->
            <i
              *ngIf="hasChildren(item) && !item.drillDown"
              class="bi nav-chevron"
              [class.bi-chevron-down]="!item.expanded"
              [class.bi-chevron-up]="item.expanded"
            ></i>
            <i
              *ngIf="hasChildren(item) && item.drillDown"
              class="bi bi-chevron-right nav-chevron"
            ></i>
          </div>

          <!-- Sub-menu items (only for inline expand, not drill-down) -->
          <ul
            *ngIf="hasChildren(item) && !item.drillDown"
            class="nav-sub-list"
            [class.expanded]="item.expanded"
          >
            <li
              *ngFor="let child of item.children"
              class="nav-sub-item"
              [class.active]="child.active"
              [class.coming-soon]="child.comingSoon"
              (click)="navigateTo(child)"
            >
              <span class="nav-label">{{ child.label }}</span>
            </li>
          </ul>
        </li>
      </ul>
      <div *ngIf="section.showDivider" class="nav-divider mx-3"></div>
    </div>
  </nav>

  <!-- Drill-down Submenu View -->
  <nav class="sidebar-nav sidebar-submenu" *ngIf="activeSubmenu">
    <!-- Back button -->
    <div class="submenu-back" (click)="goBackFromSubmenu()">
      <i class="bi bi-arrow-left"></i>
      <span>Back</span>
    </div>

    <!-- Submenu header (section label, not clickable) -->
    <div class="submenu-header">
      <div class="nav-item-content">
        <div class="position-relative d-flex align-items-center justify-content-center">
          <i [class]="'bi ' + activeSubmenu.icon"></i>
        </div>
        <span class="nav-label">{{ activeSubmenu.label }}</span>
      </div>
    </div>
    <div class="nav-divider mx-3"></div>

    <!-- Submenu children -->
    <ul class="nav-list submenu-list ps-0">
      <ng-container *ngFor="let child of activeSubmenu.children; let last = last">
        <li class="nav-sub-item-wrapper">
          <!-- Sub-item (may have its own children) -->
          <div
            class="nav-sub-item"
            [class.active]="child.active && !hasChildren(child)"
            [class.coming-soon]="child.comingSoon && !hasChildren(child)"
            [class.has-children]="hasChildren(child)"
            [class.expanded]="child.expanded"
            (click)="navigateTo(child, $event)"
          >
            <div class="nav-sub-item-content">
              <i *ngIf="child.icon" [class]="'bi ' + child.icon"></i>
              <span class="nav-label">{{ child.label }}</span>
              <i *ngIf="isLocked(child)" class="bi bi-lock-fill nav-lock-icon" role="img" aria-label="Pro feature"></i>
            </div>
            <!-- Chevron for nested expandable items -->
            <i
              *ngIf="hasChildren(child)"
              class="bi nav-chevron"
              [class.bi-chevron-down]="!child.expanded"
              [class.bi-chevron-up]="child.expanded"
            ></i>
          </div>

          <!-- Nested children (third level) -->
          <ul
            *ngIf="hasChildren(child)"
            class="nav-nested-list"
            [class.expanded]="child.expanded"
          >
            <li
              *ngFor="let grandchild of child.children"
              class="nav-nested-item"
              [class.active]="grandchild.active"
              [class.coming-soon]="grandchild.comingSoon"
              (click)="navigateTo(grandchild)"
            >
              <span class="nav-label">{{ grandchild.label }}</span>
            </li>
          </ul>
        </li>
      </ng-container>
    </ul>
  </nav>

  <!-- Bottom Promo Section -->
  <div class="sidebar-promo">
    <div class="promo-content">
      <div class="promo-header d-flex align-items-center">
        <i class="bi bi-people"></i>
        <div class="promo-text">
          <span class="promo-title">Crop Swipe</span>
          <span class="promo-subtitle">Download the ADN Mobile App</span>
        </div>
      </div>
    </div>
  </div>
</aside>
